// Click drill-down over stacked vineyard polygons, extracted pure from
// MapView so the right-click overlap menu and the click cycle share one
// stack computation (and it can be unit-tested).
//
// Vineyards render bottom-to-top in `features` order, so the polygon stack
// under a point (top-most first) is the containing features in reverse
// order. Out-of-scope features participate exactly when the caller put
// them in `features` — the white-layer toggle gates that upstream.

// "Key vineyard": notable, GG-eligible, or classified VDP.ERSTE LAGE —
// the same test that puts the 1G badge in the detail title.
const IS_ERSTE_LAGE = /^(vdp\.?\s*)?erste\s+lage$/i;
export const isKeyVineyard = (p) => !!p.notable || !!p.vdp_gg_eligible
  || !!p.official_grosses_gewaechs_eligible
  || IS_ERSTE_LAGE.test(String(p.vdp_classification ?? '').trim());

// Ray-cast point-in-ring test; ring is an array of [lng, lat] pairs.
function pointInRing(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

export function pointInFeature(lat, lng, f) {
  const g = f.geometry;
  if (!g?.coordinates) return false;
  const inPoly = (poly) =>
    pointInRing(lat, lng, poly[0] ?? []) &&
    !poly.slice(1).some(hole => pointInRing(lat, lng, hole));
  if (g.type === 'Polygon') return inPoly(g.coordinates);
  if (g.type === 'MultiPolygon') return g.coordinates.some(inPoly);
  return false;
}

// The full polygon stack under the point, top-most first. Only polygons
// visible under the current bereich/key filters participate; a bbox index
// (id -> [s, w, n, e]) prefilters the point-in-polygon tests.
export function drillStackAt(latlng, ctx) {
  const { selectedId, selectedIds, focusBereich, showAllVineyards, features, bboxes, oosIds } = ctx;
  if (!features) return [];
  const { lat, lng } = latlng;
  const stack = [];
  for (let i = features.length - 1; i >= 0; i--) {
    const f = features[i];
    const p = f.properties ?? {};
    if (!p.id) continue;
    if (focusBereich && p.bereich !== focusBereich) continue;
    const isKey = isKeyVineyard(p);
    const isSel = p.id === selectedId || selectedIds?.has(p.id);
    // Out-of-scope polygons render whenever their toggle is on, so the
    // GWS/GG-only filter never hides them from the drill cycle.
    if (!showAllVineyards && !isKey && !isSel && !oosIds?.has(p.id)) continue;
    const bb = bboxes?.get(p.id);
    if (bb && (lat < bb[0] || lat > bb[2] || lng < bb[1] || lng > bb[3])) continue;
    if (pointInFeature(lat, lng, f)) stack.push(p.id);
  }
  return stack;
}

// A click selects the top of the stack (a VDP/GWS polygon when there is
// one); clicking the same spot again steps down to the polygon beneath the
// current selection, wrapping back to the top.
export function drillTargetAt(latlng, ctx) {
  const stack = drillStackAt(latlng, ctx);
  if (stack.length === 0) return null;
  // Site-match highlight is independent of the drill cycle, except
  // entering it: clicking a highlighted vineyard that is not the current
  // selection starts at the top of the priority stack
  if (ctx.matchIds && ctx.matchIds.has(stack[0]) && stack[0] !== ctx.selectedId) return stack[0];
  const cur = stack.indexOf(ctx.selectedId);
  return cur === -1 ? stack[0] : stack[(cur + 1) % stack.length];
}
